/** @format */
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    DestroyRef,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@ngneat/transloco';
import { MaxLength } from 'common-typescript/constants';
import { CommonSearchFilters, OtmId, SearchParameters, SearchResult } from 'common-typescript/types';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UuidService } from 'sis-common';
import { Breakpoint, BreakpointService } from '../service/breakpoint.service';
import { MAX_SEARCH_LIMIT } from './search-constants';
import { SearchFilterDirective } from './search-filters/search-filter.directive';
import { countSelectedFilters, isSearchValid } from './search-utils';
import { SearchService } from './search.service';

/**
 * See the associated README for more specific usage instructions.
 */
@Component({
    selector: 'sis-search',
    templateUrl: './search.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SearchComponent<T> implements OnInit, AfterContentInit {
    constructor(private uuidService: UuidService) {
        this.id = this.uuidService.randomUUID();
    }
    /**
     * Wrap each desired search filter component with `<ng-template sisSearchFilter>...</ng-template>`, e.g.
     * ```
     * <sis-search>
     *   <ng-template sisSearchFilter>
     *     <sis-search-filter-x/>
     *   </ng-template>
     *   <ng-template sisSearchFilter>
     *     <sis-search-filter-y/>
     *   </ng-template>
     *   ...
     * </sis-search>
     * ```
     */
    @ContentChildren(SearchFilterDirective) searchFilterTemplates: QueryList<SearchFilterDirective>;

    /**
     * Shows or hides the search text input field. Defaults to true.
     */
    @Input() isTextSearchEnabled = true;

    /**
     * If true, does not limit the width of the text search input container.
     */
    @Input() useFullWidth = false;

    /**
     * Optional custom translation key or text to be used as the aria-label of the search button
     */
    @Input() ariaLabel? = 'ARIA_LABEL.SEARCH';

    /**
     * Search results returned from the latest search. Used for displaying error messages and aria-live announcements.
     */
    @Input({ required: true }) searchResult: SearchResult<T>;

    /**
     * Optional custom translation key to be used as the guidance of the text search input
     */
    @Input() guidance? = 'SEARCH.INPUT_GUIDANCE';

    /**
     * Optional custom translation key or text to be used when no search results are found.
     */
    @Input() customErrorMessage?: string;

    /**
     * Optionally allows search filters to be used. Defaults to true.
     */
    @Input() areSearchFiltersAllowed? = true;

    /**
     * Optional heading level if subtitles are used for search filters.
     */
    @Input() subtitleLevel?: 4;

    /**
     * If true, the search filters expandable is initially closed. Defaults to false.
     */
    @Input() closedByDefault? = false;

    /**
     * Emits stored parameters on subscription and on every change. Skips emitting when invalid search parameter
     * selections.
     */
    @Output() searchTrigger = new EventEmitter<SearchParameters>();

    @ViewChild('fullTextQueryInput') set fullTextQueryElement(element: ElementRef) {
        if (element) {
            this._fullTextQueryElement = element;
        }
    }

    private _fullTextQueryElement: ElementRef;
    private breakpointService = inject(BreakpointService);
    private destroyRef = inject(DestroyRef);
    private changeDetectorRef = inject(ChangeDetectorRef);
    /**
     * Unique id for the component instance, used for accessibility attributes. Generated with random UUID
     */
    protected id: string;
    /**
     * A view specific instance of {@link SearchService}.
     */
    protected readonly searchService: SearchService<CommonSearchFilters> = inject(SearchService);
    protected readonly MAX_SEARCH_LIMIT = MAX_SEARCH_LIMIT;
    private translocoService = inject(TranslocoService);

    isMobileView$: Observable<boolean>;
    searchFilterCount$: Observable<number>;

    /**
     * Difference between current full text query length and minimum required length.
     * Positive value indicates how many more characters are needed to reach minimum length.
     */
    difference: number | null = null;
    /**
     * Displayed error message related to search input length validation.
     */
    errorMessage: string | null = null;

    ngOnInit() {
        this.isMobileView$ = this.breakpointService.breakpoint$.pipe(map(breakpoint => breakpoint < Breakpoint.MD));

        this.searchFilterCount$ = this.searchService.searchParameters$.pipe(
            map(searchParameters => countSelectedFilters(searchParameters)),
        );

        this.searchService.searchParameters$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(parameters => {
            this.handleSearch(parameters);

            const fullTextQueryLength = parameters.filters?.fullTextQuery?.length || 0;
            const minFullTextQueryLength = this.searchService.minFullTextQueryLength;
            const maxQueryLength = MaxLength.MAX_TERSE_STRING_LENGTH;

            this.difference = minFullTextQueryLength - fullTextQueryLength;

            if ((fullTextQueryLength && fullTextQueryLength < minFullTextQueryLength) || fullTextQueryLength > maxQueryLength) {
                if (this.difference === 1) {
                    this.errorMessage = 'SEARCH.TYPE_ONE_MORE_LETTER';
                } else if (this.difference === 2) {
                    this.errorMessage = 'SEARCH.TYPE_TWO_MORE_LETTERS';
                } else if (this.difference > 2) {
                    this.errorMessage = 'SEARCH.TYPE_N_MORE_LETTERS';
                } else if (fullTextQueryLength > maxQueryLength) {
                    this.errorMessage = 'SEARCH.QUERY_TEXT_TOO_LONG';
                }
            } else {
                this.errorMessage = null;
            }
        });
    }

    ngAfterContentInit() {
        // Filter templates can be projected conditionally (e.g. behind an async feature toggle), in which case they
        // appear after the initial render. Mark this OnPush component for check whenever the set of projected filters
        // changes so that late-added filters render immediately instead of only after some unrelated user interaction.
        this.searchFilterTemplates.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.changeDetectorRef.markForCheck());
    }

    protected getTextSearchInputContainerClass(parameters: SearchParameters<CommonSearchFilters>): string {
        if (this.useFullWidth) return '';
        return parameters?.filters?.universityOrgId ? 'col-lg-6' : 'col-lg-8';
    }

    private handleSearch(searchParameters: SearchParameters) {
        if (isSearchValid(searchParameters, this.searchService.minFullTextQueryLength)) {
            this.searchTrigger.emit(searchParameters);
        }
    }

    searchButtonTriggered() {
        this.searchService.patchFilters({
            fullTextQuery: this.fullTextQueryValue(),
        });
    }

    clearSearchFilters(parameters: SearchParameters) {
        this.searchService.updateFilters({
            fullTextQuery: parameters?.filters?.fullTextQuery,
            universityOrgId: parameters?.filters?.universityOrgId,
        });
        document.getElementById('full-text-query-input')?.focus();
    }

    private fullTextQueryValue() {
        return this._fullTextQueryElement?.nativeElement?.value || undefined;
    }

    searchFiltersTitle(count: number): string {
        const titleText = this.translocoService.translate('SEARCH.SEARCH_FILTERS');
        const countText = this.translocoService.translate('SEARCH.SEARCH_FILTER_COUNT', { count });
        const noPermissionText = this.translocoService.translate('SEARCH.SEARCH_FILTER_NO_PERMISSIONS');
        if (!this.areSearchFiltersAllowed) {
            return `${titleText}  (${noPermissionText})`;
        }
        if (count > 0) {
            return `${titleText} ${countText}`;
        }
        return titleText;
    }

    selectUniversity(universityOrgId: OtmId) {
        // many filters depend on selected university, so clear selections
        this.searchService.updateFilters({
            fullTextQuery: this.fullTextQueryValue(),
            universityOrgId,
        });
    }
}
